import {
  copyText,
  formatAmount,
  formatDateTime,
  isAbortError,
} from "~/common/utils"
import { useEffect, useRef, useState } from "react"
import {
  DatabaseOutlined,
  GiftOutlined,
  SearchOutlined,
  ShopOutlined,
  UserOutlined,
} from "@ant-design/icons"
import {
  Button,
  Descriptions,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from "antd"
import { Alert } from "~/components/Alert"
import type { TableProps } from "antd"
import {
  createStaffActivityGiftOpLog,
  getStaffGiftOpDetail,
  listDoudianSubscriptionOrders,
  listStaffGiftOpLogs,
} from "~/api/staff"
import { ApiRequestError } from "~/common/request"
import { GiftDetails } from "~/pages/Staff/Gift/Details"
import { MOBILE_PHONE_PATTERN } from "~/common/constants"
import { useStaffUserLookup } from "~/pages/Staff/useStaffUserLookup"
import { usePagedQuery } from "~/pages/Staff/usePagedQuery"
import styles from "./index.css"

const { Text, Title } = Typography

const orderPageSize = 10
const doudianTrialActivityCode: API.Staff.ActivityCode = "DOUDIAN_TRIAL_GIFT"
const activityGiftTypes: API.Staff.StaffGiftType[] = ["ACTIVITY_GIFT"]

type OrderSearchValues = {
  phone?: string
  shopId?: number
}

type ActivityGiftFormValues = {
  mobile: string
  orderId: string
}

type OrderFilters = {
  phone?: string
  shop_id?: number
}

const showNetworkError = (error: unknown, fallback: string) => {
  if (!isAbortError(error) && !(error instanceof ApiRequestError)) {
    void message.error(fallback)
  }
}

const copyOrderId = async (orderId: string) => {
  if (await copyText(orderId)) {
    void message.success("抖店订单ID已复制")
  } else {
    void message.error("复制失败，请手动复制订单ID")
  }
}

export const StaffDoudianGift = () => {
  const [orderSearchForm] = Form.useForm<OrderSearchValues>()
  const [giftForm] = Form.useForm<ActivityGiftFormValues>()
  const detailControllerRef = useRef<AbortController | null>(null)

  const [orderFilters, setOrderFilters] = useState<OrderFilters | null>(null)
  const [orders, setOrders] = useState<API.Staff.DoudianSubscriptionOrder[]>([])
  const [orderTotal, setOrderTotal] = useState(0)
  const [orderPage, setOrderPage] = useState(1)
  const [orderRefreshVersion, setOrderRefreshVersion] = useState(0)
  const [loadingOrders, setLoadingOrders] = useState(false)

  const { queriedUser, queryingUser, lookupUser, resetQueriedUser } =
    useStaffUserLookup()
  const [submittingGift, setSubmittingGift] = useState(false)

  const [giftDetail, setGiftDetail] =
    useState<API.Staff.StaffGiftOpLogDetailResponse | null>(null)
  const [giftDetailOpen, setGiftDetailOpen] = useState(false)
  const [loadingGiftDetail, setLoadingGiftDetail] = useState(false)

  useEffect(() => {
    if (!orderFilters) {
      setOrders([])
      setOrderTotal(0)
      setLoadingOrders(false)
      return
    }

    const controller = new AbortController()

    const loadOrders = async () => {
      setLoadingOrders(true)

      try {
        const response = await listDoudianSubscriptionOrders(
          {
            ...orderFilters,
            include_trial_gift_registered: true,
            page: orderPage - 1,
            page_size: orderPageSize,
          },
          controller.signal,
        )

        if (!controller.signal.aborted) {
          setOrders(response.data?.items || [])
          setOrderTotal(response.data?.total || 0)
        }
      } catch (error) {
        showNetworkError(error, "抖店订单加载失败，请稍后重试")
      } finally {
        if (!controller.signal.aborted) {
          setLoadingOrders(false)
        }
      }
    }

    void loadOrders()
    return () => controller.abort()
  }, [orderFilters, orderPage, orderRefreshVersion])

  const giftLogs = usePagedQuery({
    fetchPage: listStaffGiftOpLogs,
    initialFilters: {
      gift_types: activityGiftTypes,
      activity_code: doudianTrialActivityCode,
    },
    errorMessage: "权益发放记录加载失败，请稍后重试",
  })

  useEffect(() => {
    return () => {
      detailControllerRef.current?.abort()
    }
  }, [])

  const searchOrders = (values: OrderSearchValues) => {
    const phone = values.phone?.trim()
    const shopId = values.shopId

    if (!shopId && !phone) {
      void message.warning("请输入店铺ID或店铺手机号")
      return
    }

    setOrderFilters({
      ...(shopId ? { shop_id: shopId } : {}),
      ...(phone ? { phone } : {}),
    })
    setOrderPage(1)
    setOrderRefreshVersion((version) => version + 1)
  }

  const resetOrderSearch = () => {
    orderSearchForm.resetFields()
    setOrderFilters(null)
    setOrderPage(1)
  }

  const queryUser = async () => {
    let mobile: string

    try {
      const values = await giftForm.validateFields(["mobile"])
      mobile = values.mobile
    } catch {
      return
    }

    await lookupUser(mobile)
  }

  const submitActivityGift = async () => {
    let values: ActivityGiftFormValues

    try {
      values = await giftForm.validateFields()
    } catch {
      return
    }

    const userId = queriedUser?.id

    if (!userId) {
      void message.warning("请先查询并确认系统用户")
      return
    }

    const orderId = values.orderId.trim()
    Modal.confirm({
      title: "确认登记抖店体验版权益",
      content: (
        <Flex
          vertical
          gap={8}
        >
          <Text>用户手机号：{queriedUser.mobile}</Text>
          <Text>抖店订单ID：{orderId}</Text>
          <Text strong>发放权益：20 积点，有效 30 × 24 小时</Text>
          <Text type="secondary">
            同一用户和同一抖店订单均只能登记一次，登记后不支持撤销。
          </Text>
        </Flex>
      ),
      okText: "确认发放",
      cancelText: "取消",
      onOk: async () => {
        setSubmittingGift(true)

        try {
          await createStaffActivityGiftOpLog({
            user_id: userId,
            order_id: orderId,
          })
          void message.success("抖店体验版权益发放成功")
          giftForm.resetFields()
          resetQueriedUser()
          giftLogs.search(giftLogs.filters)
          setOrderRefreshVersion((version) => version + 1)
        } catch (error) {
          showNetworkError(error, "抖店体验版权益发放失败，请稍后重试")
        } finally {
          setSubmittingGift(false)
        }
      },
    })
  }

  const openGiftDetail = async (record: API.Staff.StaffGiftOpLog) => {
    if (!record.id) {
      void message.warning("缺少赠送记录ID")
      return
    }

    const controller = new AbortController()
    detailControllerRef.current?.abort()
    detailControllerRef.current = controller
    setGiftDetail(null)
    setGiftDetailOpen(true)
    setLoadingGiftDetail(true)

    try {
      const response = await getStaffGiftOpDetail(
        { op_log_id: record.id },
        controller.signal,
      )

      if (!controller.signal.aborted) {
        setGiftDetail(response.data || null)
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        showNetworkError(error, "赠送详情加载失败，请稍后重试")
        setGiftDetailOpen(false)
      }
    } finally {
      if (detailControllerRef.current === controller) {
        setLoadingGiftDetail(false)
        detailControllerRef.current = null
      }
    }
  }

  const closeGiftDetail = () => {
    detailControllerRef.current?.abort()
    detailControllerRef.current = null
    setGiftDetailOpen(false)
    setGiftDetail(null)
    setLoadingGiftDetail(false)
  }

  const orderColumns: TableProps<API.Staff.DoudianSubscriptionOrder>["columns"] =
    [
      {
        title: "抖店订单ID",
        dataIndex: "order_id",
        width: 310,
        render: (value: string) => (
          <Flex
            align="center"
            gap={4}
          >
            <Text
              className={styles.orderId}
              ellipsis={{ tooltip: value }}
            >
              {value}
            </Text>
            <Button
              size="small"
              type="link"
              onClick={() => void copyOrderId(value)}
            >
              复制
            </Button>
          </Flex>
        ),
      },
      {
        title: "店铺ID",
        dataIndex: "shop_id",
        width: 150,
      },
      {
        title: "店铺手机号",
        dataIndex: "phone",
        width: 140,
        render: (value: string) => value || "-",
      },
      {
        title: "订购商品",
        width: 220,
        render: (_, record) => record.push_sku_info.title || "-",
      },
      {
        title: "规格",
        width: 150,
        render: (_, record) => record.push_sku_info.spec_value || "-",
      },
      {
        title: "实付金额",
        dataIndex: "pay_amount",
        width: 110,
        render: (value: number) => formatAmount(value),
      },
      {
        title: "支付时间",
        dataIndex: "pay_time",
        width: 180,
        render: (value: number) => formatDateTime(value),
      },
      {
        title: "服务有效期",
        width: 340,
        render: (_, record) =>
          `${formatDateTime(record.service_start_time)} 至 ${formatDateTime(
            record.service_end_time,
          )}`,
      },
      {
        title: "权益登记",
        dataIndex: "trial_gift_registered",
        width: 110,
        render: (registered: boolean) => (
          <Tag color={registered ? "green" : "default"}>
            {registered ? "已登记" : "未登记"}
          </Tag>
        ),
      },
    ]

  const giftLogColumns: TableProps<API.Staff.StaffGiftOpLog>["columns"] = [
    {
      title: "发放时间",
      dataIndex: "created_t",
      width: 180,
      render: (value?: string) => formatDateTime(value),
    },
    {
      title: "用户手机号",
      dataIndex: "user_mobile",
      width: 140,
    },
    {
      title: "抖店订单ID",
      dataIndex: "source_doudian_order_id",
      width: 260,
      ellipsis: true,
      render: (value?: string | null) => value || "-",
    },
    {
      title: "活动",
      dataIndex: "activity_code",
      width: 220,
      render: () => "抖店体验版活动",
    },
    {
      title: "发放积点",
      dataIndex: "wallet_recharge_amount",
      width: 100,
      render: (value?: number | null) => `${value || 0} 积点`,
    },
    {
      title: "到期时间",
      dataIndex: "expires_at",
      width: 180,
      render: (value?: number | null) => formatDateTime(value),
    },
    {
      title: "操作 Staff",
      dataIndex: "staff_id",
      width: 210,
      ellipsis: true,
    },
    {
      title: "状态",
      dataIndex: "gift_status",
      width: 90,
      render: (value: API.Staff.StaffGiftStatus) => (
        <Tag color={value === "GRANTED" ? "green" : "default"}>
          {value === "GRANTED" ? "已发放" : "已撤销"}
        </Tag>
      ),
    },
    {
      title: "操作",
      width: 90,
      fixed: "right",
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => void openGiftDetail(record)}
        >
          详情
        </Button>
      ),
    },
  ]

  return (
    <Flex
      className={styles.page}
      vertical
      gap={18}
    >
      <Flex
        vertical
        gap={6}
      >
        <Title level={2}>抖店权益赠送</Title>
        <Text type="secondary">
          查询抖店支付记录，为已确认的系统用户登记一次性体验版积点权益。
        </Text>
      </Flex>

      <section className={styles.panel}>
        <Flex
          align="center"
          justify="space-between"
        >
          <Flex
            align="center"
            gap={8}
          >
            <ShopOutlined className={styles.sectionIcon} />
            <Title level={4}>抖店订购订单</Title>
          </Flex>
          <Button
            disabled={!orderFilters}
            loading={loadingOrders}
            onClick={() => setOrderRefreshVersion((version) => version + 1)}
          >
            刷新
          </Button>
        </Flex>

        <Form<OrderSearchValues>
          className={styles.searchForm}
          form={orderSearchForm}
          layout="inline"
          onFinish={searchOrders}
        >
          <Form.Item
            label="店铺ID"
            name="shopId"
          >
            <InputNumber
              className={styles.searchInput}
              min={1}
              precision={0}
              placeholder="请输入 shop_id"
            />
          </Form.Item>
          <Form.Item
            label="店铺手机号"
            name="phone"
            rules={[
              {
                pattern: MOBILE_PHONE_PATTERN,
                message: "请输入正确的手机号",
              },
            ]}
          >
            <Input
              className={styles.searchInput}
              allowClear
              maxLength={11}
              placeholder="请输入手机号"
            />
          </Form.Item>
          <Form.Item>
            <Flex gap={8}>
              <Button
                htmlType="submit"
                icon={<SearchOutlined />}
                loading={loadingOrders}
                type="primary"
              >
                检索
              </Button>
              <Button onClick={resetOrderSearch}>重置</Button>
            </Flex>
          </Form.Item>
        </Form>

        <Table
          columns={orderColumns}
          dataSource={orders}
          loading={loadingOrders}
          locale={{
            emptyText: orderFilters
              ? "未查询到抖店订购订单"
              : "请输入店铺ID或店铺手机号进行校验",
          }}
          rowKey={(record) => record.id || record.order_id}
          pagination={
            orderFilters
              ? {
                  current: orderPage,
                  pageSize: orderPageSize,
                  showSizeChanger: false,
                  total: orderTotal,
                  onChange: setOrderPage,
                }
              : false
          }
          scroll={{ x: 1_750 }}
        />
      </section>

      <section className={styles.panel}>
        <Flex
          align="center"
          gap={8}
        >
          <GiftOutlined className={styles.sectionIcon} />
          <Title level={4}>登记体验版权益</Title>
        </Flex>

        <Flex
          align="stretch"
          gap={20}
          wrap
        >
          <Form<ActivityGiftFormValues>
            className={styles.giftForm}
            form={giftForm}
            labelCol={{ flex: "110px" }}
            wrapperCol={{ flex: 1 }}
            onValuesChange={(changedValues) => {
              if ("mobile" in changedValues) {
                resetQueriedUser()
              }
            }}
          >
            <Form.Item
              label="用户手机号"
              required
            >
              <Flex gap={10}>
                <Form.Item
                  className={styles.inlineField}
                  name="mobile"
                  noStyle
                  rules={[
                    { required: true, message: "请输入用户手机号" },
                    {
                      pattern: MOBILE_PHONE_PATTERN,
                      message: "请输入正确的手机号",
                    },
                  ]}
                >
                  <Input
                    allowClear
                    maxLength={11}
                    placeholder="查询并确认系统用户"
                    prefix={<UserOutlined />}
                    onPressEnter={() => void queryUser()}
                  />
                </Form.Item>
                <Button
                  icon={<SearchOutlined />}
                  loading={queryingUser}
                  onClick={() => void queryUser()}
                >
                  查询用户
                </Button>
              </Flex>
            </Form.Item>

            {queriedUser ? (
              <Descriptions
                className={styles.userCard}
                column={2}
                size="small"
              >
                <Descriptions.Item label="用户名">
                  {queriedUser.name}
                </Descriptions.Item>
                <Descriptions.Item label="手机号">
                  {queriedUser.mobile}
                </Descriptions.Item>
                <Descriptions.Item
                  label="用户ID"
                  span={2}
                >
                  {queriedUser.id}
                </Descriptions.Item>
              </Descriptions>
            ) : null}

            <Form.Item
              label="抖店订单ID"
              name="orderId"
              normalize={(value?: string) => value?.trimStart()}
              rules={[
                { required: true, message: "请输入抖店订单ID" },
                {
                  validator: (_rule, value?: string) =>
                    value?.trim()
                      ? Promise.resolve()
                      : Promise.reject(new Error("抖店订单ID不能为空")),
                },
              ]}
            >
              <Input
                allowClear
                placeholder="从上方订单列表复制或选择订单ID"
              />
            </Form.Item>

            <Form.Item label="固定权益">
              <Flex
                className={styles.benefitCard}
                vertical
                gap={6}
              >
                <Text strong>20 积点</Text>
                <Text type="secondary">
                  登记成功后立即生效，有效期为准确的 30 × 24 小时。
                </Text>
              </Flex>
            </Form.Item>

            <Flex justify="flex-end">
              <Button
                loading={submittingGift}
                type="primary"
                onClick={() => void submitActivityGift()}
              >
                确认登记并发放
              </Button>
            </Flex>
          </Form>

          <Alert
            className={styles.rulePanel}
            description={
              <ul className={styles.ruleList}>
                <li>抖店订单必须已存在于抖店支付记录中。</li>
                <li>同一用户只能领取一次该活动权益。</li>
                <li>同一抖店订单只能用于一次权益登记。</li>
                <li>积点数量、活动编码和有效期由后端固定配置。</li>
                <li>登记成功后不支持撤销，请在提交前确认用户和订单。</li>
              </ul>
            }
            showIcon
            title="登记规则"
            mode="guide"
          />
        </Flex>
      </section>

      <section className={styles.panel}>
        <Flex
          align="center"
          justify="space-between"
        >
          <Flex
            align="center"
            gap={8}
          >
            <DatabaseOutlined className={styles.sectionIcon} />
            <Title level={4}>抖店权益发放记录</Title>
          </Flex>
          <Button
            loading={giftLogs.loading}
            onClick={giftLogs.refresh}
          >
            刷新
          </Button>
        </Flex>

        <Table
          columns={giftLogColumns}
          {...giftLogs.tableProps}
          locale={{ emptyText: "暂无抖店权益发放记录" }}
          rowKey={(record) =>
            record.id || `${record.user_id}-${record.source_doudian_order_id}`
          }
          scroll={{ x: 1_600 }}
        />
      </section>

      <Modal
        destroyOnHidden
        footer={null}
        open={giftDetailOpen}
        title="抖店权益赠送详情"
        width={1_100}
        onCancel={closeGiftDetail}
      >
        <Spin spinning={loadingGiftDetail}>
          {giftDetail ? <GiftDetails detail={giftDetail} /> : null}
        </Spin>
      </Modal>
    </Flex>
  )
}
